import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar"; // Import Sidebar component
import api from "../axiosConfig"; // Use your axios instance from axiosConfig
import SearchCard from "./SearchCard"; // Import the new SearchCard component
import Select from "react-select"; // Import React Select
import { searchSessionKey } from "../utils/searchSessionStorage";

function readJsonSession(key, fallback) {
  try {
    const raw = sessionStorage.getItem(searchSessionKey(key));
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

const Search = () => {
  const [query, setQuery] = useState(
    () => sessionStorage.getItem(searchSessionKey("searchQuery")) || ""
  );
  const [results, setResults] = useState(() => readJsonSession("searchResults", []));
  const [savedProfessors, setSavedProfessors] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [sideNavOpen, setSideNavOpen] = useState(true);

  // Filter state
  const [departments, setDepartments] = useState([]);
  const [schools, setSchools] = useState([]);
  const [selectedDepartments, setSelectedDepartments] = useState(() =>
    readJsonSession("selectedDepartments", [])
  );
  const [selectedSchools, setSelectedSchools] = useState(() =>
    readJsonSession("selectedSchools", [])
  );

  // Pagination state
  const [currentPage, setCurrentPage] = useState(
    () => parseInt(sessionStorage.getItem(searchSessionKey("currentPage")), 10) || 1
  );
  const [totalPages, setTotalPages] = useState(
    () => parseInt(sessionStorage.getItem(searchSessionKey("totalPages")), 10) || 1
  );
  const [pageInput, setPageInput] = useState(currentPage); // To manage the page input value
  const [searchCompleted, setSearchCompleted] = useState(false);

  const toggleSaved = async (professor) => {
    const professorId = professor.id;
    if (savedProfessors.has(professorId)) {
      await unmarkSaved(professorId);
      return;
    }
    await markSaved(professorId);
  };

  const markSaved = async (professorId) => {
    setSavedProfessors((prev) => new Set(prev).add(professorId));
    try {
      await api.post("/handle-interaction/", {
        professor_id: professorId,
        interaction_type: "saved",
      });
    } catch (error) {
      console.error("Error saving professor:", error);
      setSavedProfessors((prev) => {
        const next = new Set(prev);
        next.delete(professorId);
        return next;
      });
      throw error;
    }
  };

  const unmarkSaved = async (professorId) => {
    setSavedProfessors((prev) => {
      const next = new Set(prev);
      next.delete(professorId);
      return next;
    });
    try {
      await api.post("/unmark-saved/", { professor_id: professorId });
    } catch (error) {
      console.error("Error unsaving professor:", error);
      setSavedProfessors((prev) => new Set(prev).add(professorId));
      throw error;
    }
  };

  const handleSearch = async (e, page = 1) => {
    if (e) e.preventDefault();
    setLoading(true);
    setErrorMessage("");

    try {
      const response = await api.get(`/search/`, {
        params: {
          q: query,
          departments: selectedDepartments.map((dept) => dept.value),
          schools: selectedSchools.map((school) => school.value),
          page: page,
          page_size: 30,
        },
      });
      setResults(response.data.results);
      setSavedProfessors(
        new Set(response.data.results.filter((prof) => prof.saved).map((prof) => prof.id))
      );
      setTotalPages(response.data.total_pages);
      setCurrentPage(page);
      setPageInput(page); // Update page input field with the current page
      setSearchCompleted(true);
    } catch (error) {
      setErrorMessage("Error fetching search results. Please try again.");
      console.error("Error fetching search results:", error);
    } finally {
      setLoading(false);
    }
  };

  // Fetch departments and schools from the server for filtering
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const schoolResponse = await api.get("/get-school-names/");
        setSchools(
          schoolResponse.data.school_names.map((school) => ({
            value: school,
            label: school,
          }))
        );

        const disciplineResponse = await api.get("/get-program-disciplines/");
        setDepartments(
          disciplineResponse.data.program_disciplines.map((discipline) => ({
            value: discipline,
            label: discipline,
          }))
        );
      } catch (error) {
        console.error("Error fetching filters:", error);
      }
    };

    fetchFilters();
  }, []);

  // Save and restore session storage (scoped per logged-in user — see searchSessionStorage.js)
  useEffect(() => {
    sessionStorage.setItem(searchSessionKey("searchQuery"), query);
    sessionStorage.setItem(searchSessionKey("searchResults"), JSON.stringify(results));
    sessionStorage.setItem(
      searchSessionKey("selectedDepartments"),
      JSON.stringify(selectedDepartments)
    );
    sessionStorage.setItem(
      searchSessionKey("selectedSchools"),
      JSON.stringify(selectedSchools)
    );
    sessionStorage.setItem(searchSessionKey("currentPage"), String(currentPage));
    sessionStorage.setItem(searchSessionKey("totalPages"), String(totalPages));
  }, [query, results, selectedDepartments, selectedSchools, currentPage, totalPages]);

  // Pagination controls
  const PaginationControls = () => (
    <div className="flex flex-wrap justify-center items-center gap-2 mt-4">
      <button
        onClick={() => handleSearch(null, currentPage - 1)}
        disabled={currentPage === 1}
        className="px-4 py-2 bg-gray-300 rounded-lg disabled:opacity-50"
      >
        Previous
      </button>
      <input
  type="number"
  value={pageInput}
  onChange={(e) => setPageInput(e.target.value)}
  onKeyPress={(e) => {
    if (e.key === "Enter") {
      const page = Math.max(1, Math.min(totalPages, parseInt(pageInput)));
      handleSearch(null, page);
    }
  }}
  className="w-16 p-2 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-[#0196BF] focus:ring-opacity-50 focus:border-[#0196BF] focus:outline-none hover:border-[#0196BF] transition-all"
/>

      <span className="text-gray-700 mx-2">
        of {totalPages}
      </span>
      <button
        onClick={() => handleSearch(null, currentPage + 1)}
        disabled={currentPage === totalPages}
        className="px-4 py-2 bg-gray-300 rounded-lg disabled:opacity-50"
      >
        Next
      </button>
    </div>
  );

  return (
    <div className="flex flex-col nav:flex-row h-screen">
      <Sidebar
        sideNavOpen={sideNavOpen}
        toggleSideNav={() => setSideNavOpen(!sideNavOpen)}
      />

      <div className="flex-1 flex flex-col overflow-x-hidden p-4 nav:p-6 px-4 nav:px-8">
        <h1 className="text-2xl nav:text-3xl font-semibold">Search Professors</h1>
        {errorMessage && <p className="text-red-500 mt-4">{errorMessage}</p>}

        <form onSubmit={(e) => handleSearch(e, 1)} className="mt-4 flex flex-col nav:flex-row nav:items-center gap-3 nav:gap-4 mb-2">
  <input
    type="text"
    className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0196BF] focus:ring-opacity-50 focus:border-[#0196BF] focus:outline-none hover:border-[#0196BF] transition-all"
    placeholder="Search..."
    value={query}
    onChange={(e) => setQuery(e.target.value)}
    style={{
      borderColor: query ? '#0196BF' : '', // Optional: Apply custom border color if needed
      boxShadow: query ? '0 0 0 2px rgba(1, 150, 191, 0.3)' : '', // Reduce opacity of the box shadow
    }}
  />
  <button
    type="submit"
    className="bg-[#0196BF] text-white px-4 py-2 rounded-lg hover:bg-[#017BA3] transition z-20 w-full nav:w-auto"
  >
    Search
  </button>
</form>

        {/* Filter Row */}
<div className="flex flex-col nav:flex-row gap-3 nav:gap-4 mb-4 items-stretch nav:items-center">
  <div className="w-full nav:max-w-sm">
    <Select
      isMulti
      value={selectedDepartments}
      onChange={setSelectedDepartments}
      options={departments}
      placeholder="All Departments"
      className="react-select-container"
      classNamePrefix="react-select"
      styles={{
        control: (base, state) => ({
          ...base,
          borderColor: state.isFocused ? '#0196BF' : base.borderColor, // Blue border on focus
          boxShadow: state.isFocused ? '0 0 0 2px rgba(1, 150, 191, 0.5)' : base.boxShadow, // Blue shadow on focus
          '&:hover': {
            borderColor: '#0196BF', // Blue border on hover
          },
        }),
        option: (base, state) => ({
          ...base,
          backgroundColor: state.isSelected
            ? '#0196BF' // Blue background for selected option
            : state.isFocused
            ? 'rgba(1, 150, 191, 0.1)' // Light blue background on hover
            : base.backgroundColor,
          color: state.isSelected ? 'white' : base.color, // White text for selected option
          '&:hover': {
            backgroundColor: 'rgba(1, 150, 191, 0.3)', // Blue hover background
            color: 'black', // Black text on hover
          },
        }),
        multiValue: (base) => ({
          ...base,
          backgroundColor: '#f3f4f6', // Light gray background for tags
          color: '#333', // Black text for tags
        }),
        multiValueLabel: (base) => ({
          ...base,
          color: '#333', // Black text for tags
        }),
        multiValueRemove: (base) => ({
          ...base,
          '&:hover': {
            backgroundColor: 'rgba(1, 150, 191, 0.5)', // Blue background on hover
            color: 'white', // White text on hover
          },
        }),
        menuList: (base) => ({
          ...base,
          maxHeight: 220,
          paddingTop: 4,
          paddingBottom: 8,
        }),
      }}
    />
  </div>

  <div className="w-full nav:max-w-sm">
    <Select
      isMulti
      value={selectedSchools}
      onChange={setSelectedSchools}
      options={schools}
      placeholder="All Schools"
      className="react-select-container"
      classNamePrefix="react-select"
      styles={{
        control: (base, state) => ({
          ...base,
          borderColor: state.isFocused ? '#0196BF' : base.borderColor, // Blue border on focus
          boxShadow: state.isFocused ? '0 0 0 2px rgba(1, 150, 191, 0.5)' : base.boxShadow, // Blue shadow on focus
          '&:hover': {
            borderColor: '#0196BF', // Blue border on hover
          },
        }),
        option: (base, state) => ({
          ...base,
          backgroundColor: state.isSelected
            ? '#0196BF' // Blue background for selected option
            : state.isFocused
            ? 'rgba(1, 150, 191, 0.1)' // Light blue background on hover
            : base.backgroundColor,
          color: state.isSelected ? 'white' : base.color, // White text for selected option
          '&:hover': {
            backgroundColor: 'rgba(1, 150, 191, 0.3)', // Blue hover background
            color: 'black', // Black text on hover
          },
        }),
        multiValue: (base) => ({
          ...base,
          backgroundColor: '#f3f4f6', // Light gray background for tags
          color: '#333', // Black text for tags
        }),
        multiValueLabel: (base) => ({
          ...base,
          color: '#333', // Black text for tags
        }),
        multiValueRemove: (base) => ({
          ...base,
          '&:hover': {
            backgroundColor: 'rgba(1, 150, 191, 0.5)', // Blue background on hover
            color: 'white', // White text on hover
          },
        }),
        menuList: (base) => ({
          ...base,
          maxHeight: 220,
          paddingTop: 4,
          paddingBottom: 8,
        }),
      }}
    />
  </div>
</div>

        {loading ? (
          <p>Loading... Please wait.</p>
        ) : (
          <div>
            {results.length === 0 && searchCompleted ? (
              <p className="mt-8 text-center text-gray-600 text-lg">
                No results found. Try different keywords or filters.
              </p>
            ) : (
              results.map((professor) => (
                <SearchCard
                  key={professor.id}
                  professor={professor}
                  toggleSaved={toggleSaved}
                  isInitiallySaved={savedProfessors.has(professor.id)}
                />
              ))
            )}
            {results.length > 0 && <PaginationControls />}
          </div>
        )}
      </div>
    </div>
  );
};

export default Search;
